/**
 * Hook React Query da busca global FTS — story E3.2.1.
 *
 * Consome ``GET /api/v1/search/global`` (story E3.1.3). O endpoint já aplica
 * permission filter + isolamento por empresa — o frontend NUNCA reimplementa
 * isso; o que volta já é seguro de exibir.
 */
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import api from "@/services/api";
import { SEARCHABLE_ENTITY_TYPES } from "@/lib/globalSearch";

/** Espelha ``GlobalSearchResultItem`` do backend (schemas/search_global.py). */
export interface GlobalSearchResult {
  entity_type: string;
  entity_id: string;
  title: string | null;
  snippet: string | null;
  owner_user_id: string | null;
  updated_at: string;
  rank: number;
}

/** Espelha ``GlobalSearchResponse`` do backend. */
export interface GlobalSearchResponse {
  query: string;
  results: GlobalSearchResult[];
  has_more: boolean;
  searched_entity_types: string[];
  /** True quando os resultados vieram do fallback de typo (pg_trgm) — story E3.2.2. */
  fuzzy: boolean;
}

/** Mínimo de caracteres para disparar busca (espelha ``min_length=2`` do endpoint). */
export const MIN_QUERY_LENGTH = 2;

/**
 * Serializa params com arrays como chaves repetidas (``key=v1&key=v2``), que é
 * o que ``Query(List[str])`` do FastAPI espera — o default do axios usa
 * ``key[]=v1`` e o backend não aceita. Sem adicionar ``qs`` como dependência.
 */
function serializeParams(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) value.forEach((v) => sp.append(key, String(v)));
    else sp.set(key, String(value));
  });
  return sp.toString();
}

/**
 * Busca FTS global pela ``query`` (já debounced pelo componente).
 *
 * Só dispara quando ``query.trim().length >= MIN_QUERY_LENGTH``. Restringe a
 * busca aos tipos com rota navegável (``SEARCHABLE_ENTITY_TYPES``) → todo
 * resultado tem destino real (zero link morto). ``keepPreviousData`` mantém a
 * lista anterior visível enquanto o usuário redigita (sem flicker).
 */
export function useGlobalSearch(query: string) {
  const q = query.trim();
  const enabled = q.length >= MIN_QUERY_LENGTH;

  return useQuery<GlobalSearchResponse>({
    queryKey: ["global-search", q],
    queryFn: async () => {
      const r = await api.get<GlobalSearchResponse>("/search/global", {
        params: { q, entity_types: SEARCHABLE_ENTITY_TYPES },
        paramsSerializer: serializeParams,
      });
      return r.data;
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
